import type {
	BrowserCapabilities,
	PreferredCodec,
	PreferredSize,
	SelectedSource,
	VideoData,
} from "../types.js";

const DEFAULT_SIZE_ORDER: PreferredSize[] = ["md", "lg", "sm", "full"];

function pickSize<T extends Record<string, string | undefined>>(
	sizes: T | undefined,
	prefer?: PreferredSize,
): string | undefined {
	if (!sizes) return undefined;
	// Prefer explicit size
	if (prefer && sizes[prefer]) return sizes[prefer]!;
	// Fallback priority order
	for (const size of DEFAULT_SIZE_ORDER) {
		if (sizes[size]) return sizes[size]!;
	}
	// Any
	const first = Object.values(sizes).find(Boolean);
	return first;
}

export function selectOptimalSource(
	videoData: VideoData,
	capabilities: BrowserCapabilities,
	preferCodec?: PreferredCodec,
	preferSize?: PreferredSize,
): SelectedSource | null {
	const variants = videoData.variants_data?.variants || {};

	// Explicit preference first
	if (preferCodec === "HLS" && variants.HLS) {
    const hlsSrc = variants.HLS.cdn_master_playlist_url || variants.HLS.full_size;
    if (hlsSrc) {
      return {
          src: hlsSrc,
          type: "application/vnd.apple.mpegurl",
          method: capabilities.canPlayHLS ? "native_hls" : "hls",
          adaptiveBitrate: true,
      };
    }
	}
	if (preferCodec === "DASH" && variants.DASH) {
    const dashSrc = variants.DASH.cdn_manifest_url || variants.DASH.full_size;
    if (dashSrc) {
      return {
          src: dashSrc,
          type: "application/dash+xml",
          method: "dash",
          adaptiveBitrate: true,
      };
    }
	}
	if (preferCodec === "WEBM" && variants.WEBM) {
		const src = pickSize(variants.WEBM, preferSize);
		if (src) {
			return {
				src,
				type: "video/webm",
				method: "progressive_webm",
				upgradeToStreaming: true,
			};
		}
	}
	if (preferCodec === "MP4" && variants.PROGRESSIVE_MP4) {
		const src = pickSize(variants.PROGRESSIVE_MP4, preferSize);
		if (src) {
			return {
				src,
				type: "video/mp4",
				method: "progressive_mp4",
				upgradeToStreaming: true,
			};
		}
	}

	// Auto-selection based on capabilities - mobile priority: HLS > WebM > MP4 > Fallback
	if (capabilities.canPlayHLS && variants.HLS) {
    const hlsSrc = variants.HLS.cdn_master_playlist_url || variants.HLS.full_size;
    if (hlsSrc) {
      return {
          src: hlsSrc,
          type: "application/vnd.apple.mpegurl",
          method: "native_hls",
          adaptiveBitrate: true,
      };
    }
	}

	if (capabilities.canPlayWebM && variants.WEBM) {
		const src = pickSize(variants.WEBM, preferSize);
		if (src) {
			return {
				src,
				type: "video/webm",
				method: "progressive_webm",
				upgradeToStreaming: true,
			};
		}
	}

	// Fallback to MP4
	const mp4 = pickSize(variants.PROGRESSIVE_MP4, preferSize);
	if (mp4) {
		return {
			src: mp4,
			type: "video/mp4",
			method: "progressive_mp4",
			upgradeToStreaming: true,
		};
	}

	// Non-native HLS support (via hls.js) if MSE is supported
	if (capabilities.supportsMSE && variants.HLS) {
    const hlsSrc = variants.HLS.cdn_master_playlist_url || variants.HLS.full_size;
    if (hlsSrc) {
      return {
          src: hlsSrc,
          type: "application/vnd.apple.mpegurl",
          method: "hls",
          adaptiveBitrate: true,
      };
    }
	}

	// DASH support if MSE is available
	if (capabilities.supportsMSE && variants.DASH) {
    const dashSrc = variants.DASH.cdn_manifest_url || variants.DASH.full_size;
    if (dashSrc) {
      return {
          src: dashSrc,
          type: "application/dash+xml",
          method: "dash",
          adaptiveBitrate: true,
      };
    }
	}

	// Final fallback - use fallback_url if no variants are available
	if (videoData.fallback_url) {
		return {
			src: videoData.fallback_url,
			type: "video/mp4", // Assume MP4 for fallback unless we can detect otherwise
			method: "fallback",
		};
	}

	// No playable variants or fallback available
	return null;
}
